import type { TranslationKey, TranslationParams } from '@okclawpro/i18n'

export type ConversationStatusTranslator = (
  key: TranslationKey,
  params?: TranslationParams,
) => string

/** Playful processing labels shared by the desktop and web chat surfaces. */
export const PROCESSING_MESSAGE_KEYS = [
  'chat.processing.thinking',
  'chat.processing.pondering',
  'chat.processing.contemplating',
  'chat.processing.reasoning',
  'chat.processing.processing',
  'chat.processing.computing',
  'chat.processing.considering',
  'chat.processing.reflecting',
  'chat.processing.deliberating',
  'chat.processing.cogitating',
  'chat.processing.ruminating',
  'chat.processing.musing',
  'chat.processing.workingOnIt',
  'chat.processing.onIt',
  'chat.processing.crunching',
  'chat.processing.brewing',
  'chat.processing.connectingDots',
  'chat.processing.mullingItOver',
  'chat.processing.deepInThought',
  'chat.processing.hmm',
  'chat.processing.letMeSee',
  'chat.processing.oneMoment',
  'chat.processing.holdOn',
  'chat.processing.bearWithMe',
  'chat.processing.justASec',
  'chat.processing.hangTight',
  'chat.processing.gettingThere',
  'chat.processing.almost',
  'chat.processing.working',
  'chat.processing.busy',
  'chat.processing.whirring',
  'chat.processing.churning',
  'chat.processing.percolating',
  'chat.processing.simmering',
  'chat.processing.cooking',
  'chat.processing.baking',
  'chat.processing.stirring',
  'chat.processing.spinningUp',
  'chat.processing.warmingUp',
  'chat.processing.revving',
  'chat.processing.buzzing',
  'chat.processing.humming',
  'chat.processing.ticking',
  'chat.processing.clicking',
  'chat.processing.whizzing',
  'chat.processing.zooming',
  'chat.processing.zipping',
  'chat.processing.chugging',
  'chat.processing.trucking',
  'chat.processing.rolling',
] as const satisfies readonly TranslationKey[]

/** Localize known provider status messages while preserving unknown diagnostics. */
export function localizeConversationStatus(
  message: string | undefined,
  statusType: string | undefined,
  t: ConversationStatusTranslator,
): string {
  const normalized = message?.trim() ?? ''

  // Compaction completion updates the existing activity and keeps its original statusType.
  if (/^compacted (?:context|conversation)/i.test(normalized)) return t('chat.processing.compacted')

  if (statusType === 'compacting' || /^compacting (?:context|conversation)/i.test(normalized)) {
    return t('chat.processing.compacting')
  }

  if (statusType === 'retrying' || /^retrying\b/i.test(normalized)) {
    const attempt = normalized.match(/attempt\s+(\d+)\s*\/\s*(\d+)/i)
    return attempt?.[1] && attempt[2]
      ? t('chat.processing.retryingAttempt', { attempt: attempt[1], max: attempt[2] })
      : t('chat.processing.retrying')
  }

  if (/^authenticating/i.test(normalized)) return t('chat.processing.authenticating')
  if (/^interrupted\.?$/i.test(normalized)) return t('chat.processing.interrupted')

  return normalized
}
